import { Fragment, type ReactNode } from 'react';

import { cn } from '@/_shared/lib/utils';

import { ConnectorPill } from './ConnectorPill';
import { groupChildren, groupOp, isGroup, removeAt, updateAt } from './lib/tree';
import type { GroupNode, Predicate } from './types';

/** The AND/OR connector a child renders in its lead-in slot — `null` for the first child (which keeps its own
 *  lead-in) and for every carded child (carded joins are standalone, between cards). */
export type ChildConnector = { op: 'and' | 'or' } | null;

/** How a group renders each child — supplied by the recursive renderer so GroupCard never imports the
 *  dispatcher (keeps the tree acyclic). Receives the child, a scoped `onChange`, an `onRemove`, and the inline
 *  connector to show in its lead-in slot (or `null`). */
export type RenderChild = (
  child: Predicate,
  onChildChange: (next: Predicate) => void,
  onChildRemove: () => void,
  connector: ChildConnector,
) => ReactNode;

/** The AND/OR container. Children stack top-to-bottom, joined by the group's combinator. A child is wrapped in
 *  a **callout card** (grey-25 fill + grey-50 border + an op-colored accent rail — blue/brand AND · green OR,
 *  with a standalone AND/OR connector between cards) when this is the **root** or when the group contains a
 *  nested sub-group — so a group-of-groups reads as distinct boxes instead of a flat, ambiguous connector run
 *  (`[A AND B] OR [C AND D]` becomes two OR-joined cards, each an inline AND clause). A group of only leaves
 *  renders **inline**: the first child keeps its lead-in, every later sibling shows the AND/OR connector in its
 *  lead-in slot. The connector is a **static label** in this slice — editing the operator is deferred. This
 *  slice only renders + edits + removes what it receives (no "+ condition" / "+ group"). Empty group ⇒ null. */
export function GroupCard({
  group,
  onChange,
  renderChild,
  isRoot = false,
}: {
  group: GroupNode;
  onChange: (next: GroupNode) => void;
  renderChild: RenderChild;
  isRoot?: boolean;
}) {
  const children = groupChildren(group);
  const op = groupOp(group);
  const childApi = (i: number) =>
    [(next: Predicate) => onChange(updateAt(group, i, next)), () => onChange(removeAt(group, i))] as const;

  if (children.length === 0) return null;

  // Card the children at the root, or whenever a sub-group is present (so nested AND/OR groups render as their
  // own boxes). A group of only leaves stays an inline clause list with lead-in connectors.
  const carded = isRoot || children.some(isGroup);

  if (!carded) {
    return (
      <div className="flex flex-col gap-2" role="group" aria-label={`${op === 'and' ? 'AND' : 'OR'} group`}>
        {children.map((child, i) => (
          <div key={i} className="min-w-0">
            {renderChild(child, ...childApi(i), i === 0 ? null : { op })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" role="group" aria-label={`${op === 'and' ? 'AND' : 'OR'} group`}>
      {children.map((child, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <div className="pl-1">
              <ConnectorPill op={op} />
            </div>
          )}
          <ClauseGroupCard op={children.length === 1 ? undefined : op}>
            {renderChild(child, ...childApi(i), null)}
          </ClauseGroupCard>
        </Fragment>
      ))}
    </div>
  );
}

/** One member of a carded group: a callout box (grey-25 fill + grey-50 border) with an accent rail whose
 *  colour is the JOINING operator — blue/brand AND · green/success OR. A card with no sibling (a single
 *  condition, or a one-child group) has no combinator to convey, so `op` is omitted and the rail is a
 *  neutral grey. Nests recursively for group-of-group children. */
export function ClauseGroupCard({ op, children }: { op?: 'and' | 'or'; children: ReactNode }) {
  return (
    <div
      data-slot="clause-group"
      className="flex items-stretch gap-3 rounded-lg border border-grey-50 bg-grey-25 py-2.5 pr-3 pl-2.5"
    >
      <span
        aria-hidden
        className={cn(
          'w-1 shrink-0 self-stretch rounded-full',
          op === 'and' ? 'bg-brand' : op === 'or' ? 'bg-success' : 'bg-grey-200',
        )}
      />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
